import React from 'react';
import type { TreeNode } from '../types';

interface UsePathResolutionV2Props {
  data: TreeNode | TreeNode[];
  selectedNodeId?: string;
  nodeKeyName: string;
  isLoadingData: boolean;
  onNodeExpandClicked: (node: TreeNode, forceProbe?: boolean) => void;
  onNodeSelected: (node: TreeNode | null, nodeId: string | null) => void;
  stopAtParent?: boolean;
  uiDisplayPath?: string;
  // Compatibility props (not used in V2 but kept for interface parity)
  lastUpdatedAt?: number | string;
  pathInputValue?: string;
  isIDBased?: boolean;
  /**
   * Whether this connector saves only the leaf ID in path (like Google Drive).
   * When true:
   * - selectedNodeId contains only the leaf folder/file ID (not a full path)
   * - uiDisplayPath contains the human-readable path for breadcrumb display
   * - Probe calls should use the leaf ID
   * - Path resolution should use uiDisplayPath for breadcrumb segments
   */
  isConnectorWithLeafIdOnly?: boolean;
  /**
   * Saved ID-based path from source/sink config (e.g., ui.id_path).
   * For leaf-ID-only connectors (Google Drive), this is the full hierarchical ID path
   * that was previously resolved. When available, skips iterative probing.
   */
  uiIdPath?: string;
  /**
   * Callback when the full ID path is resolved for leaf-ID-only connectors.
   * Parent should save this as ui.id_path in the source/sink config.
   */
  onIdPathResolved?: (idPath: string) => void;
}

// === HELPERS ===

/**
 * Safely get display name from a node with null checks
 */
const getNodeDisplayName = (node: TreeNode | null | undefined): string => {
  if (!node) return '';
  return String(
    (node as any)?.displayName || (node as any)?.display_name || node?.name || '',
  ).trim();
};

/**
 * Safely get node ID with null checks
 */
const getNodeId = (node: TreeNode | null | undefined): string => {
  if (!node) return '';
  return String(node?.id || '').trim();
};

/**
 * Folder guard: many connectors omit `type` on folder nodes.
 * Treat as folder-like unless an explicit non-folder type is present.
 */
const isFolderLike = (node: TreeNode | null | undefined): boolean => {
  if (!node) return false;
  const t = (node as any)?.type;
  return !t || t === 'folder';
};

/**
 * usePathResolutionV2 - Simplified path resolution hook
 *
 * CORE RESPONSIBILITIES:
 * 1. Track breadcrumb path for folder navigation
 * 2. Determine what data to display (currentData) based on current location
 * 3. Handle UI mode switching (breadcrumbs vs manual path input)
 * 4. Resolve paths after probes complete
 * 5. Support both standard path-based and ID-based (Google Drive) connectors
 *
 * NOT RESPONSIBLE FOR:
 * - Making probe API calls (parent component handles this via onNodeExpandClicked)
 * - Storing probe data (Redux handles this)
 * - Probe call rate limiting (parent component's responsibility)
 *
 * KEY CONCEPTS:
 *
 * SYNTHETIC NODES:
 * Temporary TreeNode objects created from saved path/display path strings to show
 * breadcrumbs immediately while waiting for probe responses. Marked with _synthetic flag.
 * Replaced with real nodes from Redux tree once probes complete.
 *
 * PROBE TRACKING:
 * - pendingInitialProbeRef: The path currently being probed
 * - completedInitialProbeRef: Set of paths already probed (never probe twice)
 * - pendingPath state: When set, indicates probing in progress (shows loading indicator)
 *
 * PATH TYPES:
 * - Standard connectors: path = "folder1/folder2/folder3" (hierarchical path)
 * - ID-based connectors: path may be an ID, displayPath is human-readable
 * - Leaf-ID-only (Google Drive): path = "leafId", requires iterative resolution
 */

/**
 * STATE MACHINE: Iterative ID Path Resolution (Google Drive)
 *
 * PURPOSE:
 * Google Drive sources/sinks save only the leaf folder ID (e.g., "abc123xyz") because
 * Google Drive IDs are globally unique. However, the UI needs the full hierarchical ID path
 * (e.g., "rootId/parentId/leafId") to navigate the folder tree. This state machine manages
 * the iterative probing process to build that full path from the display path.
 *
 * STATE TRANSITIONS:
 * 1. INACTIVE (null) - Not needed for this connector/edit
 * 2. ACTIVE (isActive: true) - Currently probing segments one by one
 * 3. COMPLETE (isComplete: true) - Successfully resolved all segments
 * 4. FAILED (isFailed: true) - A segment wasn't found in the tree
 *
 * FLOW:
 * 1. Effect 1a: Initialize when editing Google Drive without saved ui.id_path
 * 2. Effect 1b: Repeatedly runs after each probe, resolving one segment at a time
 * 3. When complete: ui.id_path is saved for future edits (optimization)
 * 4. Next edit: Effect 1 uses saved ui.id_path directly (no iterative resolution needed)
 *
 * FIELDS:
 * - displaySegments: Array of folder/file names from ui.display_path (e.g., ["My Drive", "Documents", "Reports"])
 * - resolvedNodes: Array of TreeNode objects we've resolved so far (with their IDs)
 * - currentDepth: Index of the segment we're currently trying to resolve
 * - isActive: True while probing is in progress
 * - isComplete: True when all segments resolved successfully
 * - isFailed: True if we couldn't find a segment
 * - failedSegment: Name of the segment that couldn't be found (for error display)
 */
interface IterativeIdPathResolution {
  displaySegments: string[]; // Display names from uiDisplayPath (e.g., ["My Drive", "Documents", "File.csv"])
  resolvedNodes: TreeNode[]; // Nodes we've resolved so far (with their Google Drive IDs)
  currentDepth: number; // Current segment index being resolved (0-based)
  isActive: boolean; // Whether iterative resolution is currently running
  isComplete: boolean; // Whether resolution completed successfully
  isFailed: boolean; // Whether resolution failed (segment not found)
  failedSegment: string | null; // Which segment failed (if any)
}

export const usePathResolutionV2 = ({
  data,
  selectedNodeId,
  nodeKeyName,
  isLoadingData,
  onNodeExpandClicked,
  onNodeSelected,
  stopAtParent = false,
  uiDisplayPath,
  lastUpdatedAt: _lastUpdatedAt,
  pathInputValue: _pathInputValue,
  isIDBased: _isIDBased,
  isConnectorWithLeafIdOnly = false,
  uiIdPath,
  onIdPathResolved,
}: UsePathResolutionV2Props) => {
  // === STATE ===
  const [breadcrumbPath, setBreadcrumbPath] = React.useState<TreeNode[]>([]);
  const [showBreadcrumbs, setShowBreadcrumbs] = React.useState(true);
  const [pendingPath, setPendingPath] = React.useState<string | null>(null);
  const [lastNavigateFailed, setLastNavigateFailed] = React.useState(false);
  const [lastMissingSegment, setLastMissingSegment] = React.useState<string | null>(null);
  // Track the actual selected node for display purposes (even when stopAtParent is true)
  const [selectedNodeForDisplay, setSelectedNodeForDisplay] = React.useState<TreeNode | null>(null);
  // Track iterative ID path resolution for leaf-ID-only connectors (Google Drive)
  const [iterativeIdResolution, setIterativeIdResolution] =
    React.useState<IterativeIdPathResolution | null>(null);

  // Computed flag: Are we currently in the middle of iterative resolution?
  const isIterativeResolutionInProgress = React.useMemo(() => {
    if (!iterativeIdResolution) return false;
    return (
      iterativeIdResolution.isActive &&
      !iterativeIdResolution.isComplete &&
      !iterativeIdResolution.isFailed
    );
  }, [iterativeIdResolution]);

  // === REFS ===
  const onNodeExpandClickedRef = React.useRef(onNodeExpandClicked);

  // SIMPLE STATE MACHINE FOR INITIAL PROBE:
  // 1. pendingInitialProbeRef = path we're waiting for probe response
  // 2. completedInitialProbeRef = paths we've FINISHED probing (never probe again)
  // Once a probe request is made and response received, we're DONE - no more probes
  const pendingInitialProbeRef = React.useRef<string | null>(null);
  const completedInitialProbeRef = React.useRef<Set<string>>(new Set());

  // Track if user is in manual edit mode (clicked edit, hasn't clicked check)
  const isManualEditModeRef = React.useRef(false);
  // Track if user is actively navigating (to prevent Effect 1 from resetting breadcrumbs)
  const isUserNavigatingRef = React.useRef(false);
  // Track previous loading state to detect when probe completes
  // Initialize with current isLoadingData to handle initial render correctly
  const wasLoadingRef = React.useRef(isLoadingData);

  // Keep callback ref updated
  React.useEffect(() => {
    onNodeExpandClickedRef.current = onNodeExpandClicked;
  }, [onNodeExpandClicked]);

  // === HELPERS ===

  /**
   * Find a node in the tree by ID (with null safety)
   */
  const findNodeInTree = React.useCallback(
    (nodes: TreeNode | TreeNode[] | null | undefined, nodeId?: string): TreeNode | null => {
      if (!nodeId || !nodes) return null;

      // Defensive: probe trees can occasionally contain cycles (same JS object reused as its own descendant).
      //
      // Why this happens (observed in Google Drive):
      // - The backend can sometimes return the same "id" both as a folder node and as a file node
      //   nested inside that folder (folder-id == child-file-id).
      // - If the UI normalizes/dedupes nodes by id, it can accidentally reuse the same object
      //   instance, creating a self-referential structure (cycle).
      //
      // We guard by *object identity* (WeakSet), not by id/path strings, so path-based connectors
      // like S3 with repeated names (e.g. a/a/a/a) are not affected.
      const visited = new WeakSet<object>();

      const walk = (ns: TreeNode | TreeNode[]): TreeNode | null => {
        const items = Array.isArray(ns) ? ns : [ns];
        for (const item of items) {
          if (!item) continue;
          if (typeof item === 'object') {
            if (visited.has(item as any)) continue;
            visited.add(item as any);
          }

          if (getNodeId(item) === nodeId || item.id === nodeId) return item;

          const children = (item as any)?.[nodeKeyName];
          if (children && Array.isArray(children)) {
            const found = walk(children as TreeNode[]);
            if (found) return found;
          }
        }
        return null;
      };

      return walk(nodes);
    },
    [nodeKeyName],
  );

  /**
   * Check if root level has a "/" node (FTP/Dropbox style)
   */
  const hasSlashRoot = React.useCallback(() => {
    if (!data) return false;
    const items = Array.isArray(data) ? data : [data];
    return items.some((item) => {
      if (!item) return false;
      const name = getNodeDisplayName(item);
      const id = getNodeId(item);
      return name === '/' || id === '/';
    });
  }, [data]);

  /**
   * Get children of a node safely
   */
  const getNodeChildren = React.useCallback(
    (node: TreeNode | null | undefined): TreeNode[] => {
      if (!node) return [];
      const children = (node as any)?.[nodeKeyName];
      if (!children) return [];
      if (Array.isArray(children)) return children as TreeNode[];
      return [];
    },
    [nodeKeyName],
  );

  /**
   * Some connectors (notably Google Drive) can return a non-array "children" object (e.g. `{}`)
   * to represent a known-empty folder. We must distinguish:
   * - **unknown**: children is null/undefined (needs probing)
   * - **known**: children is an array (possibly empty) OR an object (treat as known-empty)
   *
   * This is critical to avoid infinite probing loops during iterative ID resolution.
   */
  const getNodeChildrenStatus = React.useCallback(
    (node: TreeNode | null | undefined): { childrenKnown: boolean; children: TreeNode[] } => {
      if (!node) return { childrenKnown: false, children: [] };
      const rawChildren = (node as any)?.[nodeKeyName];
      if (rawChildren == null) return { childrenKnown: false, children: [] };
      if (Array.isArray(rawChildren))
        return { childrenKnown: true, children: rawChildren as TreeNode[] };
      if (typeof rawChildren === 'object') return { childrenKnown: true, children: [] };
      return { childrenKnown: false, children: [] };
    },
    [nodeKeyName],
  );

  /**
   * Internal helper to resolve a path in the tree (used by getDisplayData)
   * This avoids circular dependencies since resolvePathInTree comes later in the file
   */
  const resolvePathInTreeInternal = React.useCallback(
    (pathStr: string | null | undefined): { nodes: TreeNode[]; missing: string | null } => {
      if (!pathStr) return { nodes: [], missing: null };

      // Parse segments (inline logic to avoid dependency on hasSlashRoot)
      const rawSegments = pathStr.split('/').filter((s) => s);
      const dataArray = Array.isArray(data) ? data : data ? [data] : [];
      const hasSlash = dataArray.some((item) => {
        if (!item) return false;
        const nodeId = getNodeId(item);
        return nodeId === '/' || item.name === '/' || (item as any).displayName === '/';
      });
      const segments = pathStr.startsWith('/') && hasSlash ? ['/', ...rawSegments] : rawSegments;

      if (segments.length === 0) return { nodes: [], missing: null };

      const resolved: TreeNode[] = [];
      let currentNodes: TreeNode[] = dataArray;

      for (const segment of segments) {
        const segLower = segment.toLowerCase();
        const found = currentNodes.find((node) => {
          if (!node) return false;
          const nodeName = getNodeDisplayName(node).toLowerCase();
          const nodeId = getNodeId(node).toLowerCase();
          return nodeName === segLower || nodeId === segLower || nodeId === pathStr.toLowerCase();
        });

        if (!found) {
          return { nodes: resolved, missing: segment };
        }

        resolved.push(found);

        // `found` can match via the full-path fallback (nodeId === pathStr) on an
        // EARLY segment when a single node's own id already equals the entire
        // target path (e.g. a probe response collapses "bucket/2026" into one
        // flat node instead of nesting "bucket" -> "2026"). That node already
        // represents the whole remaining path, so stop walking segments against
        // its real children — otherwise the leftover segments never match and
        // we report a false "missing" for a path that was already found.
        if (getNodeId(found).toLowerCase() === pathStr.toLowerCase()) {
          return { nodes: resolved, missing: null };
        }

        currentNodes = getNodeChildren(found);
      }

      return { nodes: resolved, missing: null };
    },
    [data, getNodeChildren],
  );

  /**
   * Get display data - handles "/" root expansion for FTP/Dropbox
   * Shows children of the selected node (for edit mode) or last breadcrumb node
   */
  const getDisplayData = React.useCallback((): TreeNode[] => {
    // If navigation failed (path doesn't exist), show empty to avoid showing stale data
    // BUT: Don't show empty while iterative resolution is in progress
    if (lastNavigateFailed && !isIterativeResolutionInProgress) {
      return [];
    }

    // If we're actively loading data, return empty to show loading indicator
    if (isLoadingData) {
      return [];
    }

    if (!data) return [];
    const dataArray = Array.isArray(data) ? data : data ? [data] : [];

    // If at root level (no breadcrumb path)
    if (breadcrumbPath.length === 0) {
      // Check for single "/" root node - show its children instead
      if (dataArray.length === 1) {
        const rootNode = dataArray[0];
        if (!rootNode) return [];
        const rootId = getNodeId(rootNode);
        const rootName = getNodeDisplayName(rootNode);

        if (rootId === '/' || rootName === '/') {
          return getNodeChildren(rootNode);
        }
      }
      return dataArray.filter(Boolean);
    }

    // If we have a selected node for display (from edit mode probe), show its contents
    // This handles the case where stopAtParent=true but we still want to see selected folder's contents
    if (selectedNodeForDisplay) {
      // During iterative resolution with synthetic nodes, return empty (loading indicator will show)
      const isSelectedSynthetic =
        selectedNodeForDisplay._synthetic ||
        (selectedNodeForDisplay.id && String(selectedNodeForDisplay.id).startsWith('synthetic_'));
      if (isIterativeResolutionInProgress && isSelectedSynthetic) {
        return [];
      }

      // First try direct lookup by ID
      let actualSelectedNode = findNodeInTree(data, selectedNodeForDisplay.id);

      // If not found and ID looks like a path (contains '/'), try resolving the path
      if (!actualSelectedNode && selectedNodeForDisplay.id?.includes('/')) {
        const { nodes } = resolvePathInTreeInternal(selectedNodeForDisplay.id);
        if (nodes.length > 0) {
          actualSelectedNode = nodes[nodes.length - 1];
        }
      }

      if (actualSelectedNode) {
        const children = getNodeChildren(actualSelectedNode);
        return children;
      }

      // If selected node not found in tree yet (still loading), return empty
      if (isLoadingData) {
        return [];
      }
    }

    // Not at root - show children of last breadcrumb node
    const lastNode = breadcrumbPath[breadcrumbPath.length - 1];
    if (!lastNode) return [];

    // During iterative resolution, if last node is synthetic, return empty (loading indicator will show)
    const isSynthetic =
      lastNode._synthetic || (lastNode.id && String(lastNode.id).startsWith('synthetic_'));
    if (isIterativeResolutionInProgress && isSynthetic) {
      return [];
    }

    const actualNode = findNodeInTree(data, lastNode.id);
    const children = actualNode ? getNodeChildren(actualNode) : [];

    return children;
  }, [
    data,
    breadcrumbPath,
    selectedNodeForDisplay,
    findNodeInTree,
    getNodeChildren,
    isLoadingData,
    lastNavigateFailed,
    isIterativeResolutionInProgress,
    resolvePathInTreeInternal,
  ]);

  /**
   * Parse a path string into segments
   */
  const parsePathSegments = React.useCallback(
    (pathStr: string | null | undefined): string[] => {
      if (!pathStr) return [];
      // Trim each segment — getNodeDisplayName() below trims node display
      // names before matching, so an untrimmed saved segment (e.g. a Drive
      // folder name with a trailing space, a real Drive artifact) would
      // never equal its trimmed live counterpart and fail resolution.
      const segments = pathStr
        .split('/')
        .map((s) => s.trim())
        .filter((s) => s);
      if (pathStr.startsWith('/') && hasSlashRoot()) {
        return ['/', ...segments];
      }
      return segments;
    },
    [hasSlashRoot],
  );

  /**
   * Resolve a path string to nodes in the tree
   * Returns { nodes: resolved path, missing: first missing segment }
   */
  const resolvePathInTree = React.useCallback(
    (pathStr: string | null | undefined): { nodes: TreeNode[]; missing: string | null } => {
      if (!pathStr) return { nodes: [], missing: null };
      const segments = parsePathSegments(pathStr);
      if (segments.length === 0) return { nodes: [], missing: null };

      const resolved: TreeNode[] = [];
      let currentNodes: TreeNode[] = Array.isArray(data) ? data : data ? [data] : [];

      for (const segment of segments) {
        const segLower = segment.toLowerCase();
        const found = currentNodes.find((node) => {
          if (!node) return false;
          const nodeName = getNodeDisplayName(node).toLowerCase();
          const nodeId = getNodeId(node).toLowerCase();
          return nodeName === segLower || nodeId === segLower || nodeId === pathStr.toLowerCase();
        });

        if (!found) {
          return { nodes: resolved, missing: segment };
        }

        resolved.push(found);

        // See resolvePathInTreeInternal above: `found` can match via the
        // full-path fallback on an early segment when a single node's own id
        // already equals the entire target path (a probe response can
        // collapse "bucket/2026" into one flat node). Stop walking segments
        // against that node's children — it already IS the full path, so
        // continuing would spuriously report the remaining segments missing.
        if (getNodeId(found).toLowerCase() === pathStr.toLowerCase()) {
          return { nodes: resolved, missing: null };
        }

        currentNodes = getNodeChildren(found);
      }

      return { nodes: resolved, missing: null };
    },
    [data, parsePathSegments, getNodeChildren],
  );

  // === DERIVED STATE ===
  const currentData = React.useMemo(() => getDisplayData(), [getDisplayData]);

  // === EFFECTS ===

  /**
   * Effect 0: Auto-expand single root folder with children already loaded
   *
   * PROBLEM:
   * When creating a new source/destination, the initial probe (depth=1, no path) can return:
   * 1. Multiple buckets/folders at root → User must click to navigate (correct behavior)
   * 2. Single bucket/folder with its children already loaded (non-empty) → Annoying to force user
   *    to click since the response already contains everything we'd get from probing that folder
   *
   * SOLUTION:
   * After transitioning from credential screen to source/destination config screen (create mode),
   * if the initial probe response contains exactly one folder with valid non-empty children,
   * automatically navigate into it as if the user had made a probe call for that folder.
   *
   * GUARDS:
   * - Only in create mode (no selectedNodeId - not editing an existing source)
   * - Only at root (breadcrumbPath is empty)
   * - Only when data has exactly one item that is a folder
   * - Only when that folder already has children loaded AND children array is not empty
   * - Only when user is not actively navigating (prevents interference)
   * - Only runs once (uses a ref to track if already expanded)
   */
  const hasAutoExpandedRef = React.useRef(false);

  React.useEffect(() => {
    // CRITICAL: Only in create mode (no selected node = new source/destination)
    // In edit mode, we must load the user's saved path, NOT auto-expand
    if (selectedNodeId) return;

    // Only at root (no breadcrumb path)
    if (breadcrumbPath.length > 0) return;

    // Only if user is not navigating
    if (isUserNavigatingRef.current) return;

    // Only run once
    if (hasAutoExpandedRef.current) return;

    // Skip if data is not ready
    if (!data || isLoadingData) return;

    // Get root items
    const dataArray = Array.isArray(data) ? data : [data];

    // Must have exactly one item at root
    if (dataArray.length !== 1) return;

    const singleItem = dataArray[0];
    if (!singleItem) return;

    // Must be a folder
    if (!isFolderLike(singleItem)) return;

    // Skip "/" root nodes - they're already auto-expanded in getDisplayData
    const itemId = getNodeId(singleItem);
    const itemName = getNodeDisplayName(singleItem);
    if (itemId === '/' || itemName === '/') return;

    // Must already have children loaded AND children must not be empty
    const { childrenKnown, children } = getNodeChildrenStatus(singleItem);
    if (!childrenKnown) return;
    if (children.length === 0) return; // Don't auto-expand if empty

    // Mark that we've auto-expanded to prevent running again
    hasAutoExpandedRef.current = true;

    // Auto-navigate into this single folder
    // Inline implementation (can't use handleFolderNavigate due to dependency ordering)
    const newBreadcrumbPath = [singleItem];
    setBreadcrumbPath(newBreadcrumbPath);
    setShowBreadcrumbs(true);
    setPendingPath(null);
    setLastNavigateFailed(false);
    setLastMissingSegment(null);

    // Build display path
    const displayName = getNodeDisplayName(singleItem) || getNodeId(singleItem);
    const fullDisplayPath = displayName;

    // CRITICAL: Mark this path as already probed to prevent redundant probe calls
    // This prevents Effect 1 or other effects from probing a path we already have data for
    const nodePathId = singleItem.path || singleItem.id || fullDisplayPath;
    completedInitialProbeRef.current.add(nodePathId);
    // Also add the node ID separately in case it's used for probing
    if (singleItem.id && singleItem.id !== nodePathId) {
      completedInitialProbeRef.current.add(singleItem.id);
    }

    // Set selected node for display and notify parent
    setSelectedNodeForDisplay(singleItem);
    const normalizedNode: TreeNode = {
      ...singleItem,
      path: singleItem.path || singleItem.id || fullDisplayPath,
      displayPath: fullDisplayPath,
      // Signal to parent: children already loaded, no probe needed
      // Mark as scanned with children so SelectBucket's _handleNodeExpandClicked skips probing
      scanned: true,
      // Also set the flag for parent components that check this explicitly
      _childrenAlreadyLoaded: true,
    };
    onNodeSelected(normalizedNode, singleItem.id || null);

    // DON'T fire probe - we already have the children data in the response!
    // This is the whole point: avoid redundant probe call that would return the same data
    // onNodeExpandClickedRef.current(singleItem, true); // ← REMOVED
  }, [
    selectedNodeId,
    breadcrumbPath.length,
    data,
    isLoadingData,
    getNodeChildrenStatus,
    onNodeSelected,
  ]);

  /**
   * Effect 1a: Initialize iterative ID path resolution for leaf-ID-only connectors (Google Drive)
   *
   * PROBLEM:
   * Google Drive sources/sinks save only the leaf folder/file ID in `source_config.path` (e.g., "abc123xyz")
   * because Google Drive IDs are globally unique. However, the UI needs the full hierarchical ID path
   * (e.g., "rootId/parentId/leafId") to properly navigate the folder tree and show breadcrumbs.
   *
   * SOLUTION:
   * When editing a Google Drive source/sink that doesn't have `ui.id_path` saved (legacy sources),
   * this effect initiates an iterative probing process that:
   * 1. Splits the saved `ui.display_path` (e.g., "My Drive/Documents/Reports") into segments
   * 2. Probes the root to find the first segment ("My Drive")
   * 3. Uses that folder's ID to probe the next level to find "Documents"
   * 4. Continues until all segments are resolved and we have the full ID path
   * 5. Saves the result as `ui.id_path` so future edits skip this iterative process
   *
   * OPTIMIZATION:
   * Once `ui.id_path` is saved, Effect 1 (standard path resolution) uses it directly,
   * avoiding the iterative probing process entirely.
   *
   * This effect only runs for:
   * - Leaf-ID-only connectors (currently only Google Drive)
   * - Edit mode (selectedNodeId exists)
   * - Legacy sources without ui.id_path saved
   */
  React.useEffect(() => {
    // Only for leaf-ID-only connectors (Google Drive)
    if (!isConnectorWithLeafIdOnly) return;

    // Only in edit mode (has selectedNodeId)
    if (!selectedNodeId) return;

    // Only if selectedNodeId is just an ID (no slashes)
    if (selectedNodeId.includes('/')) return;

    // Only if we have a display path to resolve
    if (!uiDisplayPath) return;

    // If we already have saved ID path, skip iterative resolution
    if (uiIdPath) return;

    // Skip if user is actively navigating (they're changing the path)
    if (isUserNavigatingRef.current) return;

    // Don't re-initialize if already in progress
    if (
      iterativeIdResolution &&
      iterativeIdResolution.isActive &&
      !iterativeIdResolution.isComplete
    ) {
      return;
    }

    // Don't restart if already completed for this path
    if (iterativeIdResolution && iterativeIdResolution.isComplete) {
      return;
    }

    // CRITICAL: Don't restart if already failed for this path.
    // Without this check, a failed resolution would cause an infinite loop:
    // Effect 1b fails -> sets isFailed=true -> Effect 1a re-initializes -> Effect 1b fails again -> ...
    if (iterativeIdResolution && iterativeIdResolution.isFailed) {
      return;
    }

    // Initialize iterative ID path resolution
    const segments = parsePathSegments(uiDisplayPath);

    if (segments.length === 0) {
      // No segments to resolve
      return;
    }

    // Immediately show synthetic breadcrumb nodes from display path
    // This provides instant feedback while probing happens
    const syntheticNodes: TreeNode[] = [];
    let currentDisplayPath = '';

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      // Build cumulative display path
      if (segment === '/') {
        currentDisplayPath = '/';
      } else {
        currentDisplayPath = currentDisplayPath ? `${currentDisplayPath}/${segment}` : segment;
      }

      syntheticNodes.push({
        id: `synthetic_${i}`, // Temporary ID, will be replaced when resolved
        name: segment,
        displayName: segment,
        type: i === segments.length - 1 ? 'folder' : 'folder', // Last one might be file
        displayPath: currentDisplayPath,
        _synthetic: true, // Mark as synthetic for reference
      });
    }

    // Set breadcrumb path immediately so user sees the path while probing
    setBreadcrumbPath(syntheticNodes);
    setSelectedNodeForDisplay(syntheticNodes[syntheticNodes.length - 1]);
    setShowBreadcrumbs(true);

    setIterativeIdResolution({
      displaySegments: segments,
      resolvedNodes: [],
      currentDepth: 0,
      isActive: true,
      isComplete: false,
      isFailed: false,
      failedSegment: null,
    });

    // Set pendingPath to keep isResolvingPath true throughout iterative resolution
    setPendingPath(uiDisplayPath);

    // Always force a fresh root probe before resolving segment 0.
    // Previously this only probed when `data` was empty, so a root tree
    // already cached in Redux from earlier in the session (e.g. browsing
    // this credential for a different source/sink) was trusted as-is —
    // the one level with no freshness guarantee, unlike every deeper
    // segment which Effect 1b always re-probes. A stale/incomplete cached
    // root listing missing the target top-level folder made segment 0
    // fail immediately ("Path not found in data store") even though the
    // folder genuinely exists, since a failed resolution latches and never
    // retries for this mount (see the isFailed guard above). forceProbe
    // (true) bypasses SelectBucket's probe-count limiter, so this is safe
    // to call unconditionally.
    const rootNode: TreeNode = {
      id: '',
      name: '',
      type: 'folder',
      // Intentionally omit 'path' to trigger root probe
    };
    onNodeExpandClickedRef.current(rootNode, true);
    // Note: Effect 1b will handle the actual iterative probing once we have root data
    // and will replace synthetic nodes with real ones as they're resolved
  }, [
    isConnectorWithLeafIdOnly,
    selectedNodeId,
    uiDisplayPath,
    uiIdPath,
    iterativeIdResolution,
    parsePathSegments,
  ]);

  /**
   * Effect 1b: Progress iterative ID path resolution (Google Drive)
   *
   * This is the "workhorse" effect that drives the iterative probing process initiated by Effect 1a.
   * It runs repeatedly as probe responses arrive, resolving one segment at a time.
   *
   * ALGORITHM:
   * For each iteration:
   * 1. Get the current segment to resolve (e.g., "Documents" when currentDepth=1)
   * 2. Determine where to search:
   *    - If currentDepth=0: Search in root data
   *    - Otherwise: Search in children of the last resolved node
   * 3. Find a node matching the current segment (case-insensitive name match)
   * 4. If not found: Mark as failed and exit early
   * 5. If found:
   *    - Add to resolvedNodes array
   *    - If this is the LAST segment: Complete immediately (see CRITICAL FIX below)
   *    - Otherwise: Probe the found node and wait for next iteration
   *
   * CRITICAL FIX - Eliminating the "No files or folders found" Flash:
   * When the penultimate probe returns, it includes the final folder as a child. This effect
   * finds it and could add it to resolvedNodes. Originally, we would increment currentDepth
   * and wait for the next effect run to complete. However, this created a gap where:
   * - The final node existed in the tree
   * - getDisplayData would show it
   * - But we hadn't probed it yet, so it had no children
   * - Result: Brief flash of "No files or folders found"
   *
   * SOLUTION: When finding the last segment, complete the entire iterative resolution
   * immediately in the same effect cycle and probe the final node right away. This keeps
   * isLoadingData=true continuously, preventing any flash.
   *
   * FRESH NODE LOOKUP:
   * When searching in children of a previously resolved node, we MUST look up that node
   * fresh from the Redux tree using findNodeInTree(data, parentId), NOT use the stale
   * reference from resolvedNodes. The nodes in resolvedNodes are snapshots taken when
   * they were first found and don't have updated children after subsequent probes.
   */
  React.useEffect(() => {
    // Only run if iterative resolution is active
    if (!iterativeIdResolution || !iterativeIdResolution.isActive) return;
    if (iterativeIdResolution.isComplete || iterativeIdResolution.isFailed) return;

    // Wait for data to be ready
    if (isLoadingData) return;
    if (!data) return;

    const { displaySegments, resolvedNodes, currentDepth } = iterativeIdResolution;

    // Get current segment to resolve
    const currentSegment = displaySegments[currentDepth];

    // Get data to search in
    let searchData: TreeNode[];
    if (currentDepth === 0) {
      // First segment - search in root data
      searchData = Array.isArray(data) ? data : [data];
    } else {
      // Subsequent segments - search in children of last resolved node
      // IMPORTANT: Look up the parent fresh from the data tree, not from resolvedNodes
      // The nodes in resolvedNodes are snapshots and don't have updated children after probe
      const parentId = resolvedNodes[resolvedNodes.length - 1].id;
      const freshParent = findNodeInTree(data, parentId);

      if (!freshParent) {
        setIterativeIdResolution({
          ...iterativeIdResolution,
          isActive: false,
          isFailed: true,
          failedSegment: currentSegment,
        });

        setLastNavigateFailed(true);
        setLastMissingSegment(currentSegment);
        setPendingPath(null);
        return;
      }

      const { childrenKnown, children } = getNodeChildrenStatus(freshParent);
      searchData = children;

      // If parent children are unknown, we need to probe it first
      if (!childrenKnown && !isLoadingData) {
        onNodeExpandClickedRef.current(freshParent, true);
        return; // Wait for probe to complete
      }

      // If parent children are known-empty, we can never find the next segment.
      // IMPORTANT: do NOT probe again (prevents infinite probe loops when children is `{}` / `[]`).
      if (childrenKnown && searchData.length === 0) {
        setIterativeIdResolution({
          ...iterativeIdResolution,
          isActive: false,
          isFailed: true,
          failedSegment: currentSegment,
        });
        setLastNavigateFailed(true);
        setLastMissingSegment(currentSegment);
        setPendingPath(null);
        return;
      }
    }

    // Find node matching current segment (by display name)
    const found = searchData.find((node) => {
      const nodeName = getNodeDisplayName(node).toLowerCase();
      const segmentLower = currentSegment.toLowerCase();
      return nodeName === segmentLower;
    });

    if (!found) {
      // Segment not found - resolution failed (early exit)
      setIterativeIdResolution({
        ...iterativeIdResolution,
        isActive: false,
        isFailed: true,
        failedSegment: currentSegment,
      });

      setLastNavigateFailed(true);
      setLastMissingSegment(currentSegment);
      setPendingPath(null);

      return;
    }

    // Add found node to resolved list
    const newResolved = [...resolvedNodes, found];

    // Check if this is the last segment
    const isLastSegment = currentDepth === displaySegments.length - 1;

    if (isLastSegment) {
      // This is the last segment - complete immediately

      // Build full ID path
      const fullIdPath = newResolved.map((n) => n.id).join('/');

      // Mark as complete
      setIterativeIdResolution({
        ...iterativeIdResolution,
        resolvedNodes: newResolved,
        currentDepth: currentDepth + 1,
        isActive: false,
        isComplete: true,
      });

      // Notify parent to save this ID path
      if (onIdPathResolved) {
        onIdPathResolved(fullIdPath);
      }

      // Build full display path
      const pathSegments = newResolved
        .map((n) => getNodeDisplayName(n) || getNodeId(n))
        .filter(Boolean);
      const fullDisplayPath =
        pathSegments.length === 0
          ? ''
          : pathSegments[0] === '/'
            ? '/' + pathSegments.slice(1).join('/')
            : pathSegments.join('/');

      const normalizedNode: TreeNode = {
        ...found,
        path: found.path || found.id || fullDisplayPath,
        displayPath: fullDisplayPath,
      };

      onNodeSelected(normalizedNode, found.id || null);

      // If stopAtParent is enabled or the leaf is a file, show the parent folder contents instead.
      // Never probe a file node.
      const foundIsFolder = isFolderLike(found);
      // Only stop at parent when the resolved leaf is a file (or any explicit non-folder).
      // Do NOT stop at parent for normal folder paths (even if stopAtParent is true in edit mode).
      const shouldStopAtParentForDisplay = !foundIsFolder;
      const displayNode =
        shouldStopAtParentForDisplay && newResolved.length > 1
          ? newResolved[newResolved.length - 2]
          : found;

      if (shouldStopAtParentForDisplay) {
        // Keep breadcrumbs at the parent folder level
        setBreadcrumbPath(newResolved.length > 1 ? newResolved.slice(0, -1) : []);
        setSelectedNodeForDisplay(displayNode || null);
        // No final leaf probe needed; stop resolving immediately
        setPendingPath(null);
      } else {
        // Show full breadcrumbs and probe the final folder node to load its children
        setBreadcrumbPath(newResolved);
        setSelectedNodeForDisplay(found);
        onNodeExpandClickedRef.current(found, true);
      }

      setLastNavigateFailed(false);
      setLastMissingSegment(null);
      setShowBreadcrumbs(true);
    } else {
      // Not the last segment - continue iterating
      setIterativeIdResolution({
        ...iterativeIdResolution,
        resolvedNodes: newResolved,
        currentDepth: currentDepth + 1,
      });

      onNodeExpandClickedRef.current(found, true);
    }
  }, [
    iterativeIdResolution,
    data,
    isLoadingData,
    getNodeChildren,
    getNodeChildrenStatus,
    findNodeInTree,
    onIdPathResolved,
    onNodeSelected,
    parsePathSegments,
    stopAtParent,
  ]);

  /**
   * Effect 1: Standard initial path resolution (edit mode)
   *
   * PURPOSE:
   * When editing a source/sink, this effect handles the initial probe to load the saved path.
   * It makes ONE probe request for the configured path, then marks it as completed to prevent
   * repeated probes.
   *
   * PATH HANDLING:
   * This effect handles three scenarios:
   * 1. Path-based connectors (S3, FTP, etc.): selectedNodeId is a path like "folder1/folder2"
   * 2. ID-based connectors with uiIdPath: Use the saved full ID path (already resolved)
   * 3. Leaf-ID-only connectors (Google Drive) WITH uiIdPath: Use the optimized path
   *
   * LEAF-ID-ONLY CONNECTOR LOGIC (Google Drive):
   * - If selectedNodeId is just an ID (no slashes) AND we DON'T have uiIdPath:
   *   → Skip this effect entirely, let Effect 1a/1b handle iterative resolution
   * - If we HAVE uiIdPath saved (from previous edit):
   *   → Use uiIdPath for probing (full hierarchical ID path)
   *   → Use uiDisplayPath for breadcrumb labels
   *   → This is the OPTIMIZED PATH - skips iterative probing
   *
   * SYNTHETIC BREADCRUMBS:
   * Immediately creates and displays breadcrumb nodes from the display path (or path segments)
   * while the probe is in flight. This provides instant UI feedback. Effect 2/2b will replace
   * these synthetic nodes with real ones once the probe completes.
   *
   * PROBE TRACKING:
   * Uses refs to ensure we only probe once per path:
   * - pendingInitialProbeRef: Path currently being probed
   * - completedInitialProbeRef: Set of paths we've finished probing (never probe again)
   *
   * INTEGRATION WITH ITERATIVE RESOLUTION:
   * Guards at the top ensure this effect doesn't run during or immediately after
   * iterative resolution (Effect 1a/1b), preventing conflicts.
   */
  React.useEffect(() => {
    // Skip if iterative ID resolution is active OR just completed (for leaf-ID-only connectors)
    if (
      iterativeIdResolution &&
      (iterativeIdResolution.isActive || iterativeIdResolution.isComplete)
    ) {
      return;
    }

    // Determine if selectedNodeId looks like a path (has slashes) or just an ID
    const isSelectedNodeIdJustAnId = selectedNodeId && !selectedNodeId.includes('/');

    // CRITICAL: For leaf-ID-only connectors (Google Drive) without uiIdPath,
    // skip Effect 1 entirely and let Effect 1a/1b handle iterative resolution
    // Only probe directly if we have the optimized uiIdPath
    if (isConnectorWithLeafIdOnly && isSelectedNodeIdJustAnId && !uiIdPath) {
      return;
    }

    // For leaf-ID-only connectors (Google Drive):
    // - If we have uiIdPath, use it for probing (optimized - full ID path already resolved)
    // - Otherwise, this effect is skipped (see guard above)
    const shouldUseIdPath = isConnectorWithLeafIdOnly && isSelectedNodeIdJustAnId && uiIdPath;

    // For probing:
    // - If we have uiIdPath and should use it, use that (full ID path)
    // - If selectedNodeId is just an ID, probe with it directly (but only if not leaf-ID-only without uiIdPath)
    // - If selectedNodeId is a path, use it (even if it's display-style, backend should handle it)
    const pathToProbe = shouldUseIdPath ? uiIdPath : selectedNodeId;

    // For display:
    // - If we have uiDisplayPath, prefer it (human-readable)
    // - If selectedNodeId is just an ID, use uiDisplayPath for breadcrumb segments
    // - Otherwise fallback to selectedNodeId
    const pathForDisplay = uiDisplayPath || selectedNodeId;

    // GUARD: If we've already completed probing for this path, NEVER probe again
    if (pathToProbe && completedInitialProbeRef.current.has(pathToProbe)) {
      return;
    }

    // GUARD: If we're currently waiting for a probe response for this path, don't start another
    if (pendingInitialProbeRef.current === pathToProbe) {
      return;
    }

    // GUARD: If we have a pending probe for a DIFFERENT path, don't interrupt it
    if (pendingInitialProbeRef.current !== null) {
      return;
    }

    // Skip if user is actively navigating
    if (isUserNavigatingRef.current) {
      return;
    }

    // Skip if no path (this is create mode - no initial path to probe)
    if (!selectedNodeId || !pathToProbe) {
      return;
    }

    // ALWAYS create and show synthetic breadcrumb nodes from display path immediately
    // This provides instant feedback while probing happens
    // We'll replace these with real nodes once probe completes (in Effect 2)
    const segments = parsePathSegments(pathForDisplay);
    if (segments.length > 0) {
      let currentDisplayPath = '';
      // Also track the probe path for ID-based systems
      const probeSegments = parsePathSegments(pathToProbe);

      const syntheticNodes: TreeNode[] = segments.map((segment, index) => {
        // Build cumulative display path for each segment
        if (segment === '/') {
          currentDisplayPath = '/';
        } else {
          currentDisplayPath = currentDisplayPath ? `${currentDisplayPath}/${segment}` : segment;
        }

        // For ID-based systems, use the probe path segments for the actual ID
        // but display path segments for the name
        let currentProbePath = '';
        for (let i = 0; i <= index; i++) {
          const ps = probeSegments[i] || segments[i];
          if (ps === '/') {
            currentProbePath = '/';
          } else {
            currentProbePath = currentProbePath ? `${currentProbePath}/${ps}` : ps;
          }
        }

        return {
          id: currentProbePath, // Use probe path for ID (for tree lookups)
          name: segment, // Use display segment for name (for breadcrumb label)
          displayName: segment,
          type: 'folder' as const,
          path: currentProbePath,
          displayPath: currentDisplayPath,
          // Mark as synthetic so we know to refresh when real data arrives
          _synthetic: true,
        };
      });

      // Set synthetic breadcrumbs immediately
      setBreadcrumbPath(syntheticNodes);
      const targetNode = syntheticNodes[syntheticNodes.length - 1];
      if (targetNode) {
        setSelectedNodeForDisplay(targetNode);
      }
      setShowBreadcrumbs(true);
    }

    // Mark that we're about to probe this path (use probe path for tracking)
    pendingInitialProbeRef.current = pathToProbe;
    setPendingPath(pathToProbe);

    // Make ONE probe request for the target path using the PROBE path (not display)
    const displaySegments = parsePathSegments(pathForDisplay);
    const lastName =
      displaySegments.length > 0 ? displaySegments[displaySegments.length - 1] : pathToProbe;
    const targetNode: TreeNode = {
      id: pathToProbe, // Use actual path/ID for probing
      name: lastName || pathToProbe,
      type: 'folder',
      path: pathToProbe,
      displayPath: pathForDisplay,
    };

    onNodeExpandClickedRef.current(targetNode, true);

    // eslint-disable-next-line
  }, [selectedNodeId, uiDisplayPath, iterativeIdResolution, uiIdPath, isConnectorWithLeafIdOnly]);

  /**
   * Effect 2: Handle standard probe response (for Effect 1 probes)
   *
   * PURPOSE:
   * Detects when a probe initiated by Effect 1 completes (isLoadingData: true → false)
   * and processes the response to update breadcrumbs with real nodes.
   *
   * PROBE COMPLETION TRACKING:
   * - Watches wasLoadingRef to detect loading → not loading transitions
   * - Marks the path as COMPLETED in completedInitialProbeRef (prevents future probes)
   * - Clears pendingInitialProbeRef to allow new navigations
   *
   * PATH RESOLUTION:
   * 1. First tries to resolve pendingPath (the probe path - may be an ID path)
   * 2. If that fails and uiDisplayPath is different, tries resolving uiDisplayPath as fallback
   * 3. If successful: Replaces synthetic breadcrumbs with real nodes from tree
   * 4. If path not found (missing segment): Marks as navigation failed
   *
   * SYNTHETIC BREADCRUMB HANDLING:
   * If breadcrumbs are still synthetic after probe completes, Effect 2 waits for Effect 2b
   * to handle the resolution. This happens when Redux hasn't updated yet.
   *
   * INTEGRATION WITH ITERATIVE RESOLUTION:
   * Guards ensure this effect doesn't run during or immediately after iterative resolution.
   * Effect 1b handles probe responses for iterative probes, and Effect 2c clears pendingPath
   * after iterative resolution completes.
   */
  React.useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoadingData;

    // Skip if iterative resolution is active OR just completed - Effect 1b and Effect 2c handle those
    if (isIterativeResolutionInProgress || iterativeIdResolution?.isComplete) return;

    // Only proceed when probe completes (loading -> not loading)
    if (!pendingPath || isLoadingData) return;
    if (!wasLoading) return;

    // CRITICAL: Mark this path as COMPLETED - never probe again
    if (pendingInitialProbeRef.current === pendingPath) {
      completedInitialProbeRef.current.add(pendingPath);
      pendingInitialProbeRef.current = null;
    }

    // Now check if the path exists in the response
    // Try the probe path first, then the display path as fallback
    let { nodes, missing } = resolvePathInTree(pendingPath);

    // If probe path failed and we have a different display path, try that
    if ((nodes.length === 0 || missing) && uiDisplayPath && uiDisplayPath !== pendingPath) {
      const displayResult = resolvePathInTree(uiDisplayPath);
      if (displayResult.nodes.length > 0 && !displayResult.missing) {
        nodes = displayResult.nodes;
        missing = displayResult.missing;
      }
    }

    if (missing) {
      // Check if we have synthetic breadcrumbs (initial load) - if so, wait for Effect 2b to handle it
      const hasSyntheticBreadcrumbs = breadcrumbPath.some((node: any) => node._synthetic);

      if (!hasSyntheticBreadcrumbs) {
        // Only mark as failed if this is NOT initial load - path truly doesn't exist
        setLastNavigateFailed(true);
        setLastMissingSegment(null);
        setSelectedNodeForDisplay(null);
        if (!isManualEditModeRef.current) {
          setShowBreadcrumbs(true);
        }
        setPendingPath(null);
      }
      // If we have synthetic breadcrumbs, KEEP pendingPath set so isResolvingPath stays true
      // This prevents "No files or folders found" flash while waiting for Effect 2b
    } else if (nodes.length > 0) {
      // Path exists - update breadcrumbs
      const buildDisplayPath = (ns: TreeNode[]) => {
        const parts = ns.map((n) => getNodeDisplayName(n) || getNodeId(n)).filter(Boolean);
        if (parts.length === 0) return '';
        return parts[0] === '/' ? '/' + parts.slice(1).join('/') : parts.join('/');
      };

      const resolvedLeaf = nodes[nodes.length - 1];
      const shouldStopAtParentForDisplay = resolvedLeaf && !isFolderLike(resolvedLeaf);

      const breadcrumbNodes =
        shouldStopAtParentForDisplay && nodes.length > 1 ? nodes.slice(0, -1) : nodes;
      const displayNode =
        breadcrumbNodes.length > 0 ? breadcrumbNodes[breadcrumbNodes.length - 1] : null;

      setBreadcrumbPath(breadcrumbNodes);
      setSelectedNodeForDisplay(displayNode);

      if (shouldStopAtParentForDisplay && displayNode) {
        // Keep leaf selected for config, but show parent folder contents.
        // Prefer matching by leaf ID, fall back to display name.
        const children = getNodeChildren(displayNode);
        const leafId = selectedNodeId || '';
        const displayLeafName = (uiDisplayPath || '').split('/').filter(Boolean).pop() || '';
        const foundLeaf =
          children.find((c) => getNodeId(c) === leafId) ||
          (displayLeafName
            ? children.find(
                (c) => getNodeDisplayName(c).toLowerCase() === displayLeafName.toLowerCase(),
              )
            : null);

        const leafNode = foundLeaf || resolvedLeaf;
        if (leafNode) {
          const normalizedLeaf: TreeNode = {
            ...leafNode,
            path: (leafNode as any).path || leafNode.id || buildDisplayPath(nodes),
            displayPath: uiDisplayPath || buildDisplayPath(nodes),
          };
          onNodeSelected(normalizedLeaf, leafNode.id || null);
        }

        // If we ended up stopping at the parent because the leaf is a file (auto-stop),
        // we may have probed the file path. In that case, probe the parent folder once
        // to load its contents. If we already probed the parent, don't double-probe.
        if (isFolderLike(displayNode) && pendingPath !== displayNode.id) {
          onNodeExpandClickedRef.current(displayNode, true);
        }
      } else if (resolvedLeaf) {
        const normalizedNode: TreeNode = {
          ...resolvedLeaf,
          path: (resolvedLeaf as any).path || resolvedLeaf.id || buildDisplayPath(nodes),
          displayPath: buildDisplayPath(nodes),
        };
        onNodeSelected(normalizedNode, resolvedLeaf.id || null);
      }
      setLastNavigateFailed(false);
      setLastMissingSegment(null);
      isManualEditModeRef.current = false;
      setShowBreadcrumbs(true);
      setPendingPath(null);
    }
  }, [
    pendingPath,
    isLoadingData,
    resolvePathInTree,
    stopAtParent,
    data,
    uiDisplayPath,
    onNodeSelected,
    isIterativeResolutionInProgress,
    iterativeIdResolution,
  ]);

  /**
   * Effect 2b: Re-resolve synthetic breadcrumbs when Redux data updates
   *
   * PURPOSE:
   * Handles the race condition where Effect 2 runs before Redux has updated with probe data.
   * When that happens, Effect 2 can't find the path in the tree, so breadcrumbs remain synthetic.
   * This effect watches for data updates and retries the path resolution.
   *
   * TIMING:
   * - Only runs if breadcrumbs are still synthetic (marked with _synthetic flag)
   * - Waits for isLoadingData to be false (probe completed)
   * - Waits for data to be available (Redux updated)
   *
   * PATH RESOLUTION:
   * 1. First tries to resolve selectedNodeId (the probe path)
   * 2. If that fails and uiDisplayPath is different, tries uiDisplayPath as fallback
   * 3. If successful: Replaces synthetic breadcrumbs with real nodes
   * 4. If path not found: Marks navigation as failed
   * 5. Always clears pendingPath when done (success or failure)
   *
   * INTEGRATION WITH ITERATIVE RESOLUTION:
   * Guards ensure this doesn't interfere with iterative resolution. Effect 1b handles
   * replacing synthetic nodes during iterative probing, and Effect 2c handles cleanup
   * after iterative resolution completes.
   */
  React.useEffect(() => {
    // Skip if iterative resolution is active OR just completed - Effect 1b and Effect 2c handle those
    if (isIterativeResolutionInProgress || iterativeIdResolution?.isComplete) return;

    // Only run if we have synthetic breadcrumbs (marked with _synthetic)
    const hasSyntheticBreadcrumbs =
      breadcrumbPath.length > 0 && breadcrumbPath.some((node: any) => node._synthetic);

    if (!hasSyntheticBreadcrumbs || !data) return;

    // Don't run while still loading
    if (isLoadingData) return;

    // Try to resolve the path - try probe path first (for ID-based), then display path
    const pathToProbe = selectedNodeId;
    const pathForDisplay = uiDisplayPath || selectedNodeId;
    if (!pathToProbe) return;

    // Try probe path first
    let { nodes, missing } = resolvePathInTree(pathToProbe);

    // If probe path failed and we have a different display path, try that too
    if ((nodes.length === 0 || missing) && pathForDisplay !== pathToProbe) {
      const displayResult = resolvePathInTree(pathForDisplay);
      if (displayResult.nodes.length > 0 && !displayResult.missing) {
        nodes = displayResult.nodes;
        missing = displayResult.missing;
      }
    }

    if (nodes.length > 0 && !missing) {
      const buildDisplayPath = (ns: TreeNode[]) => {
        const parts = ns.map((n) => getNodeDisplayName(n) || getNodeId(n)).filter(Boolean);
        if (parts.length === 0) return '';
        return parts[0] === '/' ? '/' + parts.slice(1).join('/') : parts.join('/');
      };

      const resolvedLeaf = nodes[nodes.length - 1];
      const shouldStopAtParentForDisplay = resolvedLeaf && !isFolderLike(resolvedLeaf);

      const breadcrumbNodes =
        shouldStopAtParentForDisplay && nodes.length > 1 ? nodes.slice(0, -1) : nodes;
      const displayNode =
        breadcrumbNodes.length > 0 ? breadcrumbNodes[breadcrumbNodes.length - 1] : null;

      setBreadcrumbPath(breadcrumbNodes);
      setSelectedNodeForDisplay(displayNode);

      if (shouldStopAtParentForDisplay && displayNode) {
        const children = getNodeChildren(displayNode);
        const leafId = selectedNodeId || '';
        const displayLeafName = (uiDisplayPath || '').split('/').filter(Boolean).pop() || '';
        const foundLeaf =
          children.find((c) => getNodeId(c) === leafId) ||
          (displayLeafName
            ? children.find(
                (c) => getNodeDisplayName(c).toLowerCase() === displayLeafName.toLowerCase(),
              )
            : null);

        const leafNode = foundLeaf || resolvedLeaf;
        if (leafNode) {
          const normalizedLeaf: TreeNode = {
            ...leafNode,
            path: (leafNode as any).path || leafNode.id || buildDisplayPath(nodes),
            displayPath: uiDisplayPath || buildDisplayPath(nodes),
          };
          onNodeSelected(normalizedLeaf, leafNode.id || null);
        }
      } else if (resolvedLeaf) {
        const normalizedNode: TreeNode = {
          ...resolvedLeaf,
          path: (resolvedLeaf as any).path || resolvedLeaf.id || buildDisplayPath(nodes),
          displayPath: buildDisplayPath(nodes),
        };
        onNodeSelected(normalizedNode, resolvedLeaf.id || null);
      }
      setLastNavigateFailed(false);
      setPendingPath(null); // Clear pending state - resolution complete
    } else if (missing) {
      setLastNavigateFailed(true);
      setSelectedNodeForDisplay(null);
      setPendingPath(null); // Clear pending state - resolution failed
    }
  }, [
    data,
    breadcrumbPath,
    selectedNodeId,
    uiDisplayPath,
    isLoadingData,
    resolvePathInTree,
    onNodeSelected,
    isIterativeResolutionInProgress,
    iterativeIdResolution,
  ]);

  /**
   * Effect 2c: Clear pendingPath after iterative resolution completes (Google Drive)
   *
   * PURPOSE:
   * After Effect 1b completes iterative resolution and probes the final node, this effect
   * waits for that final probe to finish loading before clearing pendingPath.
   *
   * WHY THIS MATTERS:
   * - pendingPath being set keeps isResolvingPath: true
   * - isResolvingPath: true shows the loading indicator in the UI
   * - We want the loading indicator visible throughout the entire iterative resolution
   *   process, INCLUDING the final probe that loads the target folder's children
   * - Only when that final probe completes should we clear pendingPath and hide the loader
   *
   * TIMING:
   * - Only runs when iterativeIdResolution.isComplete is true (Effect 1b finished)
   * - Only runs when pendingPath is still set (not yet cleared)
   * - Waits for isLoadingData to be false (final probe completed)
   * - Then clears pendingPath, which sets isResolvingPath: false
   *
   * This is part of the fix for the "No files or folders found" flash - by keeping
   * isResolvingPath: true until the very end, we ensure the loading indicator stays
   * visible throughout the entire process.
   */
  React.useEffect(() => {
    // Only run if iterative resolution just completed
    if (!iterativeIdResolution?.isComplete) return;
    if (!pendingPath) return; // Already cleared
    if (isLoadingData) return; // Wait for final probe to finish loading

    // Final probe has completed, clear pendingPath
    setPendingPath(null);
  }, [iterativeIdResolution?.isComplete, pendingPath, isLoadingData]);

  /**
   * Effect 3: Refresh selectedNodeForDisplay when data updates after probe
   * This ensures getDisplayData can find the node and show updated children
   * Note: We don't modify breadcrumbPath here to avoid interfering with breadcrumb navigation
   */
  React.useEffect(() => {
    if (!selectedNodeForDisplay || !data) return;

    // When data updates (after probe), refresh selectedNodeForDisplay to point to the updated node
    // This ensures getDisplayData can access the latest children data
    const refreshedNode = findNodeInTree(data, selectedNodeForDisplay.id);
    if (refreshedNode) {
      // Only update if the node reference changed (has new children data)
      // Use a ref to track if we've already updated to avoid unnecessary state updates
      setSelectedNodeForDisplay(refreshedNode);
    }
  }, [data, selectedNodeForDisplay?.id, findNodeInTree]);

  /**
   * Effect 4: Update currentData when breadcrumb path changes and data loads
   * (handled via useMemo on currentData - no effect needed)
   */

  // === ACTIONS ===

  /**
   * Navigate to a path (user typed and submitted)
   * Supports optional second argument for API compatibility with V1
   * NOTE: This function handles probing - don't call onNodeExpandClicked before calling this
   */
  const navigateToPath = React.useCallback(
    (fullPath: string, _options?: { forceToLeaf?: boolean }) => {
      if (!fullPath) return;

      setLastNavigateFailed(false);
      setLastMissingSegment(null);

      // First, try to resolve in existing tree
      const { nodes, missing } = resolvePathInTree(fullPath);

      // Mark as user navigating to prevent Effect 1 from resetting
      isUserNavigatingRef.current = true;

      if (nodes.length > 0 && !missing) {
        // Fully resolved - if leaf is a file, show the parent folder contents instead
        const leaf = nodes[nodes.length - 1];
        const shouldStopAtParentForDisplay = leaf && !isFolderLike(leaf);
        const breadcrumbNodes =
          shouldStopAtParentForDisplay && nodes.length > 1 ? nodes.slice(0, -1) : nodes;
        setBreadcrumbPath(breadcrumbNodes);
        // Exit manual edit mode on successful navigation
        isManualEditModeRef.current = false;
        setShowBreadcrumbs(true);

        // Track the actual selected node for display
        const displayNode =
          breadcrumbNodes.length > 0 ? breadcrumbNodes[breadcrumbNodes.length - 1] : null;
        if (displayNode) {
          setSelectedNodeForDisplay(displayNode);
          // Probe to get fresh data for the displayed folder; never probe a file
          if (isFolderLike(displayNode)) {
            onNodeExpandClickedRef.current(displayNode, true);
          }
        } else {
          setSelectedNodeForDisplay(null);
        }
      } else {
        // Need to probe this path
        // Stay in input mode only if user is manually editing, otherwise will switch after probe
        const segments = parsePathSegments(fullPath);
        const lastName = segments.length > 0 ? segments[segments.length - 1] : fullPath;
        const targetNode: TreeNode = {
          id: fullPath,
          name: lastName || fullPath,
          type: 'folder',
          path: fullPath,
          fullPath: fullPath,
        };

        setSelectedNodeForDisplay(targetNode);
        onNodeExpandClickedRef.current(targetNode, true);
        setPendingPath(fullPath);
      }
    },
    [resolvePathInTree, parsePathSegments, stopAtParent],
  );

  /**
   * Handle breadcrumb click - navigate to that level
   * Flow: Update breadcrumb immediately → Fire fetch → Show loading → Show content
   */
  const handleBreadcrumbClick = React.useCallback(
    (index: number) => {
      if (index === -1) {
        // Root clicked - update breadcrumb immediately
        // Mark as user navigating to prevent Effect 1 from resetting
        isUserNavigatingRef.current = true;
        setBreadcrumbPath([]);
        setSelectedNodeForDisplay(null);
        // Exit manual edit mode when navigating to root
        isManualEditModeRef.current = false;
        setShowBreadcrumbs(true);
        setPendingPath(null);
        setLastNavigateFailed(false);
        setLastMissingSegment(null);
        onNodeSelected(null, null);
        return;
      }

      // Step 1: Update breadcrumb path immediately (slice to clicked level)
      const newPath = breadcrumbPath.slice(0, index + 1);

      // Rebuild path from tree to ensure fresh node references
      const rebuiltPath: TreeNode[] = [];
      let currentNodes: TreeNode[] = Array.isArray(data) ? data : data ? [data] : [];

      for (const pathNode of newPath) {
        if (!pathNode?.id) continue;
        const foundNode = currentNodes.find(
          (n) => getNodeId(n) === pathNode.id || n.id === pathNode.id,
        );
        if (foundNode) {
          rebuiltPath.push(foundNode);
          currentNodes = getNodeChildren(foundNode);
        } else {
          // If node not found in tree, use the path node as fallback
          rebuiltPath.push(pathNode);
        }
      }

      // Mark as user navigating to prevent Effect 1 from resetting
      isUserNavigatingRef.current = true;
      // Update breadcrumb immediately (before fetch)
      setBreadcrumbPath(rebuiltPath);
      // Exit manual edit mode when navigating via breadcrumbs
      isManualEditModeRef.current = false;
      setShowBreadcrumbs(true);
      setPendingPath(null);
      setLastNavigateFailed(false);
      setLastMissingSegment(null);

      // Step 2: Build full display path from the rebuilt breadcrumb path
      const pathSegments = rebuiltPath
        .map((n) => getNodeDisplayName(n) || getNodeId(n))
        .filter(Boolean);
      const fullDisplayPath =
        pathSegments.length === 0
          ? ''
          : pathSegments[0] === '/'
            ? '/' + pathSegments.slice(1).join('/')
            : pathSegments.join('/');

      // Step 3: Set selected node and fire fetch
      const node = rebuiltPath[rebuiltPath.length - 1];
      if (node) {
        setSelectedNodeForDisplay(node);
        const normalizedNode: TreeNode = {
          ...node,
          // Keep node.id for ID-based systems, but set path/displayPath to full breadcrumb path
          path: node.path || node.id || fullDisplayPath,
          displayPath: fullDisplayPath,
        };
        // Pass node.id for selection tracking, node contains the full displayPath for config
        onNodeSelected(normalizedNode, node.id || null);
        // Step 4: Fire fetch - isLoadingData will show loading state
        onNodeExpandClickedRef.current(node, true);
      }
    },
    [breadcrumbPath, data, onNodeSelected, getNodeChildren],
  );

  /**
   * Build full path string from breadcrumb path + optional selected node
   */
  const buildFullPath = React.useCallback(
    (selectedNode?: TreeNode | null): string => {
      // Build breadcrumb portion of the path
      const breadcrumbSegments = breadcrumbPath
        .map((node) => getNodeDisplayName(node) || getNodeId(node))
        .filter(Boolean);

      // If there's a selected node, append it
      if (selectedNode) {
        const selectedName = getNodeDisplayName(selectedNode) || getNodeId(selectedNode);
        if (selectedName) {
          breadcrumbSegments.push(selectedName);
        }
      }

      // Join with '/' - handle root "/" specially
      if (breadcrumbSegments.length === 0) return '';
      if (breadcrumbSegments[0] === '/') {
        return '/' + breadcrumbSegments.slice(1).join('/');
      }
      return breadcrumbSegments.join('/');
    },
    [breadcrumbPath],
  );

  /**
   * Handle folder click (single click = select)
   * Only marks the folder as selected (for config/saving), does NOT change breadcrumb or displayed content.
   * User stays at current breadcrumb level but the clicked folder is marked as the "selected path".
   * The saved path becomes: {breadcrumb path}/{selected folder}
   */
  const handleFolderClick = React.useCallback(
    (node: TreeNode) => {
      if (!node) return;
      // Mark as user navigating to prevent Effect 1 from resetting
      isUserNavigatingRef.current = true;
      // Exit manual edit mode when selecting a folder
      isManualEditModeRef.current = false;
      setShowBreadcrumbs(true);
      setPendingPath(null);
      setLastNavigateFailed(false);
      setLastMissingSegment(null);
      // DON'T change selectedNodeForDisplay - that would change the displayed content
      // Single click only marks the folder as selected for config purposes
      // Build full display path: breadcrumb path + selected folder name
      const fullDisplayPath = buildFullPath(node);
      const normalizedNode: TreeNode = {
        ...node,
        // Keep node.id for ID-based systems, but set path/displayPath to full breadcrumb path
        path: node.path || node.id || fullDisplayPath,
        displayPath: fullDisplayPath,
      };
      // Pass node.id for selection tracking, node contains the full displayPath for config
      onNodeSelected(normalizedNode, node.id || null);
    },
    [onNodeSelected, buildFullPath],
  );

  /**
   * Handle folder navigate (double click = go into folder)
   * Flow: Update breadcrumb → Fire fetch → Show loading → Show content
   * ALWAYS fires probe to get fresh data for the folder.
   * The saved path becomes the full breadcrumb path (including the navigated folder).
   */
  const handleFolderNavigate = React.useCallback(
    (node: TreeNode) => {
      if (!node || node.type !== 'folder') return;

      // Mark as user navigating to prevent Effect 1 from resetting
      isUserNavigatingRef.current = true;

      // Step 1: Update breadcrumb - ALWAYS add the node to the path
      // Only skip if this node is already the LAST item in the breadcrumb (already inside it)
      const lastNode = breadcrumbPath[breadcrumbPath.length - 1];
      const isAlreadyLastInPath = lastNode?.id === node.id;
      const newBreadcrumbPath = isAlreadyLastInPath
        ? [...breadcrumbPath]
        : [...breadcrumbPath, node];

      setBreadcrumbPath(newBreadcrumbPath);
      // Exit manual edit mode when navigating via double-click
      isManualEditModeRef.current = false;
      setShowBreadcrumbs(true);
      setPendingPath(null);
      setLastNavigateFailed(false);
      setLastMissingSegment(null);

      // Step 2: Build full display path from the new breadcrumb path (which includes the node)
      const pathSegments = newBreadcrumbPath
        .map((n) => getNodeDisplayName(n) || getNodeId(n))
        .filter(Boolean);
      const fullDisplayPath =
        pathSegments[0] === '/' ? '/' + pathSegments.slice(1).join('/') : pathSegments.join('/');

      // Step 3: Set selected node for display and notify parent
      setSelectedNodeForDisplay(node);
      const normalizedNode: TreeNode = {
        ...node,
        // Keep node.id for ID-based systems, but set path/displayPath to full breadcrumb path
        path: node.path || node.id || fullDisplayPath,
        displayPath: fullDisplayPath,
      };
      // Pass node.id for selection tracking, node contains the full displayPath for config
      onNodeSelected(normalizedNode, node.id || null);

      // Step 4: ALWAYS fire probe to get fresh data - user explicitly navigated
      onNodeExpandClickedRef.current(node, true);
    },
    [breadcrumbPath, onNodeSelected],
  );

  /**
   * Switch to edit (input) mode
   * User explicitly wants to edit the path - stay in input mode until they click check
   */
  const handleEditClick = React.useCallback(() => {
    isManualEditModeRef.current = true;
    setShowBreadcrumbs(false);
  }, []);

  /**
   * Switch to breadcrumbs mode
   * User clicked check - exit manual edit mode and show breadcrumbs
   */
  const handleCheckClick = React.useCallback(() => {
    isManualEditModeRef.current = false;
    setShowBreadcrumbs(true);
  }, []);

  /**
   * Reset navigation error state
   */
  const resetNavigationState = React.useCallback(() => {
    setLastNavigateFailed(false);
    setLastMissingSegment(null);
    setPendingPath(null);
    setSelectedNodeForDisplay(null);
  }, []);

  // === RETURN ===
  return {
    // State
    path: breadcrumbPath,
    currentData,
    isNavigating: isLoadingData,
    isResolvingPath: pendingPath !== null || isIterativeResolutionInProgress,
    showBreadcrumbs,
    // Suppress error display while iterative resolution is in progress
    lastNavigateFailed: isIterativeResolutionInProgress ? false : lastNavigateFailed,
    lastMissingSegment: isIterativeResolutionInProgress ? null : lastMissingSegment,
    lastMissingIndex: null, // Simplified - not tracking index

    // Actions
    handleBreadcrumbClick,
    handleFolderClick,
    handleFolderNavigate,
    handleEditClick,
    handleCheckClick,
    navigateToPath,
    resetNavigationState,

    // Compatibility (unused in V2 but kept for interface parity)
    isPathValid: !lastNavigateFailed,
    exitManualEditMode: handleCheckClick,
    isOutsideCurrentTree: false,
    lastAttemptedPath: pendingPath,
  };
};
